"use client";

import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import PdfTextTemplate from "@/components/pdfTemplates/pdfTextTemplate";
import { IBaseLabelValue } from "@/interfaces";
import {
  IBackChequesResultData,
  IRawPurchasedBouncedCheckInquiryResData,
} from "@/interfaces/inquiries/bank";
import { cn } from "@/lib";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useRef } from "react";

const formatAmount = (value?: string | number) => {
  if (value === undefined || value === null || value === "") return "-";

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) return String(value);

  return `${numericValue.toLocaleString("fa-IR")} ریال`;
};

const isRecord = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const resolveBackChequesApiEnvelope = (
  payload: Record<string, any>,
): Record<string, any> => {
  const nestedData = payload.data;

  if (
    isRecord(nestedData) &&
    (isRecord(nestedData.result) ||
      nestedData.responseCode !== undefined ||
      nestedData.trackId !== undefined ||
      nestedData.status !== undefined)
  ) {
    return nestedData;
  }

  return payload;
};

const BouncedCheckInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedBouncedCheckInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const bouncedCheckData = data.data;
  const backChequesApiEnvelope = resolveBackChequesApiEnvelope(
    bouncedCheckData as Record<string, any>,
  );
  const bouncedCheckInput = (bouncedCheckData.input ??
    backChequesApiEnvelope.input ??
    {}) as {
    national_code?: string;
    nationalCode?: string;
    customer_type?: string;
  };
  const fullBackChequesResult = (backChequesApiEnvelope.result ??
    bouncedCheckData.result) as
    | IBackChequesResultData
    | undefined;
  const simpleBouncedCheckResult =
    isRecord(bouncedCheckData.data) && bouncedCheckData.data.count !== undefined
      ? bouncedCheckData.data
      : isRecord(backChequesApiEnvelope.data)
        ? backChequesApiEnvelope.data
        : bouncedCheckData.data;
  const nationalCode =
    bouncedCheckInput.national_code ??
    bouncedCheckInput.nationalCode ??
    fullBackChequesResult?.nid ??
    "-";
  const nationalCodeDisplay =
    nationalCode !== "-" ? nationalCode : "XXXXXXXXXX";
  const chequeList = fullBackChequesResult?.chequeList ?? [];
  const isFullBackChequesResult =
    Number(data.inquery_id) === 83 ||
    Boolean(fullBackChequesResult?.chequeList || fullBackChequesResult?.nid);
  const count = Number(simpleBouncedCheckResult?.count ?? 0);
  const hasAmountDetails =
    simpleBouncedCheckResult?.sumAmount !== undefined ||
    simpleBouncedCheckResult?.sumBouncedAmount !== undefined;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("bouncedCheck.nationalCode"),
      value: nationalCode,
    },
  ];

  if (bouncedCheckInput.customer_type) {
    inputContent.push({
      label: t("bouncedCheck.customerType"),
      value:
        bouncedCheckInput.customer_type === "1"
          ? t("bouncedCheck.person")
          : t("bouncedCheck.legal"),
    });
  }

  if (fullBackChequesResult?.name) {
    inputContent.push({
      label: "نام و نام خانوادگی",
      value: fullBackChequesResult.name,
    });
  }

  if (bouncedCheckData.trackId || simpleBouncedCheckResult?.trackId) {
    inputContent.push({
      label: t("bouncedCheck.trackId"),
      value:
        bouncedCheckData.trackId || simpleBouncedCheckResult?.trackId || "-",
    });
  }

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const resultContent =
    chequeList.length > 0 ? (
      <p className="text-justify text-sm md:text-base">
        براساس اطلاعات دریافتی، دارنده کد ملی {nationalCodeDisplay}
        <span className="mx-1 font-semibold" style={{ color: "#c10007" }}>
          دارای {chequeList.length} فقره چک برگشتی ثبت‌شده می‌باشد
        </span>
        . جزئیات چک‌ها در گزارش قابل مشاهده است.
      </p>
    ) : count > 0 ? (
      <p className="text-justify text-sm md:text-base">
        براساس اطلاعات دریافتی، دارنده کد ملی {nationalCode}
        <span className="mx-1 font-semibold" style={{ color: "#c10007" }}>
          دارای {count} فقره چک برگشتی ثبت‌شده می‌باشد
        </span>
        . توصیه می‌شود پیش از انجام هرگونه تعامل مالی، سوابق ایشان به‌صورت کامل
        بررسی گردد.
      </p>
    ) : (
      <p className="text-justify text-sm md:text-base">
        بر اساس استعلام انجام‌شده، برای دارنده کد ملی{" "}
        <strong>{nationalCodeDisplay}</strong>
        <strong className="text-success-main">
          {" "}
          هیچ‌گونه سابقه چک برگشتی ثبت‌شده در شبکه بانکی کشور وجود ندارد.
        </strong>
      </p>
    );

  return (
    <div className="shadow-main from-emerald-mint relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          {t("bouncedCheck.title")}
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="items-center md:mb-3">
          <h2 className="my-5 text-center text-lg font-bold md:my-0">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="bounced-check-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName="چک-برگشتی"
            tableTitle="چک برگشتی"
          />
        </FlexBetween>
        <div className="shadow-main mb-10 flex flex-col gap-6 rounded-xl p-4 py-8 sm:p-8 md:gap-8">
          {chequeList.length > 0 ? (
            <>
              <p className="text-sm md:text-base">
                بر اساس اطلاعات دریافتی، دارنده کد ملی{" "}
                <strong>{nationalCodeDisplay}</strong>{" "}
                <span className="text-red-700">
                  دارای {chequeList.length} فقره چک برگشتی ثبت‌شده می‌باشد.
                </span>
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse text-right text-xs md:text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600">
                      <th className="border p-3">شماره چک</th>
                      <th className="border p-3">کد رهگیری</th>
                      <th className="border p-3">مبلغ</th>
                      <th className="border p-3">بانک و شعبه</th>
                      <th className="border p-3">دلیل برگشت</th>
                      <th className="border p-3">تاریخ وصول</th>
                      <th className="border p-3">تاریخ برگشت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chequeList.map((cheque) => (
                      <tr key={`${cheque.id}-${cheque.number}`}>
                        <td className="border p-3">{cheque.number || "-"}</td>
                        <td className="border p-3">{cheque.id || "-"}</td>
                        <td className="border p-3">
                          {formatAmount(cheque.amount)}
                        </td>
                        <td className="border p-3">
                          {cheque.branchDescription || "-"}
                        </td>
                        <td className="border p-3">
                          {cheque.dishonorReason || "-"}
                        </td>
                        <td className="border p-3">{cheque.date || "-"}</td>
                        <td className="border p-3">{cheque.backDate || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : count > 0 ? (
            <p className="text-sm md:text-base">
              بر اساس اطلاعات دریافتی، دارنده کد ملی مذکور{" "}
              <span className="mx-1 text-red-700">
                دارای {count} فقره چک برگشتی
              </span>{" "}
              ثبت‌شده می‌باشد. توصیه می‌شود پیش از انجام هرگونه تعامل مالی،
              سوابق ایشان به‌صورت کامل بررسی گردد.
            </p>
          ) : (
            <p className="text-sm md:text-base">
              بر اساس استعلام انجام‌شده، برای دارنده کد ملی{" "}
              <strong>{nationalCodeDisplay}</strong>
              <strong className="text-success-main">
                {" "}
                هیچ‌گونه سابقه چک برگشتی ثبت‌شده در شبکه بانکی کشور وجود ندارد.
              </strong>
            </p>
          )}
          {hasAmountDetails && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="mb-2 text-xs text-gray-500">مجموع مبالغ چک‌ها</p>
                <p className="font-bold">
                  {formatAmount(simpleBouncedCheckResult?.sumAmount)}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="mb-2 text-xs text-gray-500">مجموع مبالغ برگشتی</p>
                <p className="font-bold">
                  {formatAmount(simpleBouncedCheckResult?.sumBouncedAmount)}
                </p>
              </div>
            </div>
          )}
        </div>
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="bounced-check-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName="چک-برگشتی"
            tableTitle="چک برگشتی"
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-bounced-check-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-bounced-check-inquiry-new-request"
              onClick={() =>
                router.push(
                  isFullBackChequesResult
                    ? "/services/استعلام-چک-برگشتی-با-جزئیات-کامل"
                    : routes.services.Bounced_Check.rewritePath,
                )
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTextTemplate
          title="نتیجه استعلام چک برگشتی"
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default BouncedCheckInquiryDetails;
